import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { PanelService } from '../api/panel.service';
import { isPlatformServer } from '@angular/common';
import { LocalStorageService } from '../storage/local-storage.service';
import { AppStateService } from '../app-state.service';
import { LocalStorageVariables } from '../../enum/localstorage-variables';
import { Exchange } from '../../models/exchange.model';
@Injectable({
  providedIn: 'root'
})
export class ExchangeResolverService {
  private panelService = inject(PanelService);
  private localStorageService = inject(LocalStorageService);
  private appStateService = inject(AppStateService);

  private exchangeDetail = new BehaviorSubject<Exchange[]>([]);
  private readonly CACHE_EXPIRY_MS = 60 * 60 * 1000; // 1 hour
  private initialized = false;

  platformId = inject(PLATFORM_ID);
  
  constructor() {
    // Defer initialization to avoid injection context issues during route resolution
    // Initialization will happen lazily when resolve() is called
  }
  
  private initializeIfNeeded(): void {
    if (this.initialized) {
      return; // Already initialized
    }
    this.initialized = true;
    
    // Load cached data on initialization (SSR-safe)
    this.loadCachedData();
    // Load from AppState if available
    const cachedExchangeDetail = this.appStateService.getExchangeDetail();
    if (cachedExchangeDetail) {
      this.exchangeDetail.next(cachedExchangeDetail);
    }
  }

  private loadCachedData(): void {
    if (isPlatformServer(this.platformId)) {
      return;
    }
    const cached = this.localStorageService.getItem(LocalStorageVariables.exchange_detail);
    if (cached && cached.data && cached.timestamp) {
      const now = Date.now();
      // Check if cache is still valid (not expired)
      if (now - cached.timestamp < this.CACHE_EXPIRY_MS) {
        this.exchangeDetail.next(cached.data);
      } else {
        // Cache expired, remove it
        this.localStorageService.deleteItem(LocalStorageVariables.exchange_detail);
      }
    }
  }

  private saveToCache(data: Exchange[]): void {
    if (isPlatformServer(this.platformId)) {
      return;
    }
    this.localStorageService.setItem(LocalStorageVariables.exchange_detail, {
      data: data,
      timestamp: Date.now()
    });
  }

  private resolveInfo$(): Observable<Exchange[]> {
    return this.panelService.getExchangeDetail().pipe(
      map((data) => {
        // Update AppState
        this.appStateService.setExchangeDetail(data);
        this.exchangeDetail.next(data);
        this.saveToCache(data);
        return data;
      }),
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  get permissionInit(): Observable<Exchange[]> {
    return this.exchangeDetail.asObservable();
  }

  set permissionInit(info: Exchange[]) {
    this.exchangeDetail.next(info);
  }

  public updateInfo(): Observable<Exchange[]> {
    return this.resolveInfo$();
  }

  public resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<Exchange[]> {
    // Initialize if needed (lazy initialization)
    this.initializeIfNeeded();
    
    // Check AppState first
    const cachedExchangeDetail = this.appStateService.getExchangeDetail();
    if (cachedExchangeDetail && cachedExchangeDetail.length > 0) {
      this.exchangeDetail.next(cachedExchangeDetail);
      return of(cachedExchangeDetail);
    }
    // If exchange detail is already loaded, return immediately (non-blocking)
    if (this.exchangeDetail.value && this.exchangeDetail.value.length > 0) {
      return of(this.exchangeDetail.value);
    }
    // Otherwise, fetch the data
    return this.resolveInfo$();
  }
}
